"use client";

import { useParams } from "next/navigation";
import { useMemo } from "react";
import { DEFAULT_LANG, LANG_PREFIX, SUPPORTED_LANGS } from "./constants";

/**
 * Client hook: given the current [lang] param, returns a function
 * that prepends the correct language prefix to a bare internal path.
 *
 * Usage:
 *   const langHref = useLangHref();
 *   <Link href={langHref("/search")}>Browse</Link>
 *   // In FR mode:  href="/search"   (default lang has no prefix)
 *   // In EN mode:  href="/en/search"
 */
export function useLangHref() {
    const params = useParams();
    const lang = params?.lang || DEFAULT_LANG;

    return useMemo(
        () => (path) => {
            // Normalize path
            let p = path || "/";
            if (!p.startsWith("/")) p = "/" + p;

            // External / anchor / mailto / tel → return as-is
            if (/^(https?:)?\/\//.test(p) || p.startsWith("#") || p.startsWith("mailto:") || p.startsWith("tel:")) {
                return p;
            }

            // If path already has a supported lang prefix → normalize it
            const existingPrefix = SUPPORTED_LANGS.find(l => p.startsWith(`/${l}`));
            if (existingPrefix) {
                // Strip existing prefix and re-apply
                p = p.replace(new RegExp(`^/${existingPrefix}`), "") || "/";
            }

            // Apply current lang prefix (default lang has no prefix)
            const prefix = LANG_PREFIX[lang] || "";
            let result = prefix + p;
            // Strip trailing slash for root path: "/en/" → "/en", keep "/" as "/"
            if (result.length > 1 && result.endsWith("/")) result = result.slice(0, -1);
            return result || "/";
        },
        [lang]
    );
}

/**
 * Server-safe version: pass the lang explicitly.
 * Used in generateMetadata and server components.
 */
export function buildLangHref(path, lang) {
    let p = path || "/";
    if (!p.startsWith("/")) p = "/" + p;

    if (/^(https?:)?\/\//.test(p) || p.startsWith("#") || p.startsWith("mailto:") || p.startsWith("tel:")) {
        return p;
    }

    // Strip existing lang prefix if present
    const existingPrefix = SUPPORTED_LANGS.find(l => p.startsWith(`/${l}`));
    if (existingPrefix) {
        p = p.replace(new RegExp(`^/${existingPrefix}`), "") || "/";
    }

    const prefix = LANG_PREFIX[lang] || "";
    let result = prefix + p;
    if (result.length > 1 && result.endsWith("/")) result = result.slice(0, -1);
    return result || "/";
}

